import * as Sentry from '@sentry/react';

import { User, Profile } from '../api/amlbotAPI/models';

const isProd = import.meta.env.MODE === 'production';

const SENTRY_CONFIG = {
  dsn: import.meta.env.VITE_REACT_APP_SENTRY_DSN,
  environment: import.meta.env.MODE || 'development',
  debug: false,
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  // Tracing
  tracesSampleRate: isProd ? 0.1 : 1.0, // Capture 100% in dev, 10% in prod
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [
    'localhost',
    // /^https:\/\/.*\.amlbot\.com/, // Commented out to disable API tracing
    // /^https:\/\/amlbot\.com/, // Commented out to disable API tracing
  ],
  // Session Replay
  replaysSessionSampleRate: isProd ? 0.01 : 0.1, // 1% in prod, 10% in dev
  replaysOnErrorSampleRate: 1.0, // Always capture replays for errors
  // Logs
  enableLogs: true,
};

const initSentry = () => {
  Sentry.init({
    ...SENTRY_CONFIG,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Additional replay options
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
  });
};

// Utility functions
export const {
  captureException,
  captureMessage,
  setUser,
  setTag,
  setContext,
  addBreadcrumb,
} = Sentry;

// Function to set user info in Sentry
export const setSentryUser = (
  userData: User | null,
  profileData?: Profile | null,
) => {
  if (userData) {
    setUser({
      id: userData.emailAddress,
      email: userData.emailAddress,
      username: userData.fullName,
      ip_address: '{{auto}}',
      // Additional user context
      extras: {
        provider: userData.provider,
        emailStatus: userData.emailStatus,
        is2faEnabled: userData.is2faEnabled,
        use2Fa: userData.use2Fa,
        extBalance: userData.extBalance,
        discount: userData.discount,
        locale: userData.locale,
        hasBillingCard: userData.hasBillingCard,
        refProgram: userData.refProgram,
        useKyc: userData.useKyc,
        canClearHistory: userData.canClearHistory,
        amlFlow: userData.amlFlow,
        useCompliance: userData.useCompliance,
        checkOnly: userData.checkOnly,
        accountMode: userData.accountMode,
        proMode: userData.proMode,
        receiveReminders: userData.receiveReminders,
        is2faAllowed: userData.is2faAllowed,
        // Profile data
        hasApiAccess: !!profileData?.accessKey,
        monitoringUsed: profileData?.usedMonitoring,
        devModeUsed: profileData?.devModeUsed,
        isB2B: profileData?.isB2B,
      },
    });

    // Set tags for better filtering
    setTag('auth_provider', userData.provider || 'unknown');
    setTag(
      'email_confirmed',
      userData.emailStatus === 'confirmed' ? 'true' : 'false',
    );
    setTag('2fa_enabled', userData.is2faEnabled ? 'true' : 'false');
    setTag('use_2fa', userData.use2Fa ? 'true' : 'false');
    setTag('has_billing_card', userData.hasBillingCard ? 'true' : 'false');
    setTag('ref_program', userData.refProgram ? 'true' : 'false');
    setTag('use_kyc', userData.useKyc ? 'true' : 'false');
    setTag('locale', userData.locale || 'unknown');
    setTag('aml_flow', userData.amlFlow || 'unknown');
    setTag('use_compliance', userData.useCompliance ? 'true' : 'false');
    setTag('check_only', userData.checkOnly ? 'true' : 'false');
    setTag('account_mode', userData.accountMode?.toString() || 'unknown');
    setTag('pro_mode', userData.proMode ? 'true' : 'false');
    setTag('receive_reminders', userData.receiveReminders ? 'true' : 'false');
    setTag('2fa_allowed', userData.is2faAllowed ? 'true' : 'false');

    if (profileData) {
      setTag('has_api_access', profileData.accessKey ? 'true' : 'false');
      setTag('monitoring_used', profileData.usedMonitoring ? 'true' : 'false');
      setTag('dev_mode', profileData.devModeUsed ? 'true' : 'false');
      setTag('is_b2b', profileData.isB2B ? 'true' : 'false');
    }
  } else {
    // Clear user data
    setUser(null);
  }
};

export { initSentry };
